/**
 * Export Manager
 *
 * Manages video export with robust error handling and retry logic
 */

import {
  WebCodecsExporter,
  ExportConfig,
  ProgressCallback,
  DEFAULT_EXPORT_CONFIG,
  ASPECT_RATIOS,
} from './webcodecs-exporter';
import { HybridExporter } from './hybrid-exporter';
import { SmartHybridExporter } from './smart-hybrid-exporter';
import { ConcatExporter } from './concat-exporter';
import { MuxingExporter } from './muxing-exporter';
import { PreRenderMuxingExporter } from './prerender-muxing-exporter';
import { Clip, Track, SubtitleStyle } from './types';
import { FFmpegOutOfMemoryError } from './ffmpeg-errors';
import { FFmpegService } from './ffmpeg-service';

type ExportStrategy = 'auto' | 'hybrid' | 'concat' | 'webcodecs' | 'muxing' | 'prerender' | 'smart-hybrid' | 'server';

type ExporterInstance = {
  export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob>;
  abort(): void;
};

export interface ExportManagerConfig extends Omit<Partial<ExportConfig>, 'strategy'> {
  maxRetries?: number;
  retryDelay?: number;
  format?: string;
  strategy?: ExportStrategy;
  concatDurationThreshold?: number;
  longClipThreshold?: number;
}

export class ExportError extends Error {
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = 'ExportError';
  }
}

/**
 * Export Manager class
 */
export class ExportManager {
  private config: ExportConfig;
  private maxRetries: number;
  private retryDelay: number;
  private currentExporter?: ExporterInstance;
  private strategy: ExportStrategy;
  private concatDurationThreshold: number;
  private longClipThreshold: number;

  constructor(config: ExportManagerConfig = {}) {
    const {
      maxRetries,
      retryDelay,
      strategy,
      concatDurationThreshold,
      longClipThreshold,
      ...exportConfig
    } = config;

    this.config = {
      ...DEFAULT_EXPORT_CONFIG,
      ...(exportConfig as Partial<ExportConfig>),
    };
    this.maxRetries = maxRetries ?? 3;
    this.retryDelay = retryDelay ?? 1000;
    this.strategy = strategy ?? 'auto';
    this.concatDurationThreshold = concatDurationThreshold ?? 15 * 60; // 15 minutes
    this.longClipThreshold = longClipThreshold ?? 10 * 60; // 10 minutes
  }

  /**
   * Export video with retry logic
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle,
    progressCallback: ProgressCallback
  ): Promise<Blob> {
    // Validate inputs
    this.validateInputs(clips, tracks);

    let lastError: Error | undefined;
    const failedStrategies = new Set<ExportStrategy>();
    let lastStrategy: ExportStrategy | null = null;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          progressCallback({
            phase: 'preparing',
            progress: 0,
            message: `Nouvelle tentative (${attempt}/${this.maxRetries})...`,
          });

          // Wait before retry
          await this.delay(this.retryDelay * attempt);
        }

        const strategy = await this.determineStrategy(
          clips,
          tracks,
          failedStrategies
        );

        console.log('[ExportManager] Strategy selected:', strategy, '| Failed strategies:', Array.from(failedStrategies));

        lastStrategy = strategy;
        this.currentExporter = this.createExporter(strategy, progressCallback);

        if (strategy === 'muxing') {
          progressCallback({
            phase: 'preparing',
            progress: 1,
            message: 'Mode Soft Export : muxing rapide sans ré-encodage (overlays ignorés)...'
          });
        }
        if (strategy === 'concat') {
          // Déterminer si on a des effets pour afficher le bon message
          const hasEffects = this.hasEffectsRequiringRender(clips, tracks);
          progressCallback({
            phase: 'preparing',
            progress: 1,
            message: hasEffects
              ? 'Mode concat avec effets : concaténation FFmpeg + overlays...'
              : 'Mode ultra rapide : aucun FX détecté, simple concat sans réencodage...'
          });
        }

        // --- FPS CHECK ---
        // Essayer de détecter le framerate de la vidéo principale
        const mainVideo = clips.find(c => c.type === 'video' && c.url && !c.clipRole);
        if (mainVideo && mainVideo.url) {
          try {
            // Check for large file reference
            const fileRef = (mainVideo as any)._file as File | undefined;
            const detectedFps = await this.detectFrameRate(mainVideo.url, fileRef);
            if (detectedFps > 0) {
              console.log(`[ExportManager] FPS détecté pour ${mainVideo.name}: ${detectedFps}`);
              this.config.fps = detectedFps;
            }
          } catch (e) {
            console.warn('[ExportManager] Impossible de détecter le FPS, fallback à 30', e);
          }
        }

        // Attempt export
        const result = await this.currentExporter.export(
          clips,
          tracks,
          activeSubtitleLanguage,
          subtitleStyle
        );

        // Success!
        this.currentExporter = undefined;
        return result;

      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (lastError instanceof FFmpegOutOfMemoryError) {
          progressCallback({
            phase: 'preparing',
            progress: 0,
            message: 'FFmpeg a manqué de mémoire. Fermeture d\'autres onglets ou réduction de la durée peut aider. Tentative d\'une stratégie alternative...'
          });
        }

        if (lastStrategy) {
          failedStrategies.add(lastStrategy);
        }

        this.currentExporter = undefined;
        lastStrategy = null;

        // Check if error is retryable
        if (!this.isRetryableError(lastError)) {
          throw new ExportError(
            `Export échoué: ${lastError.message}`,
            lastError
          );
        }

        console.warn(`Export attempt ${attempt + 1} failed with strategy ${lastStrategy}:`, lastError.message);

        // If this was the last attempt, throw
        if (attempt === this.maxRetries) {
          throw new ExportError(
            `Export échoué après ${this.maxRetries + 1} tentatives: ${lastError.message}`,
            lastError
          );
        }
      }
    }

    // Should never reach here
    throw new ExportError(
      'Export échoué pour une raison inconnue',
      lastError
    );
  }

  /**
   * Abort current export
   */
  abort(): void {
    if (this.currentExporter) {
      this.currentExporter.abort();
      this.currentExporter = undefined;
    }
  }

  /**
   * Determine the export strategy to use for this attempt
   */
  private async determineStrategy(
    clips: Clip[],
    tracks: Track[],
    failedStrategies: Set<ExportStrategy>
  ): Promise<ExportStrategy> {
    if (this.strategy !== 'auto') {
      if (!failedStrategies.has(this.strategy)) {
        return this.strategy;
      }

      // Fallback hierarchy based on chosen strategy
      if (this.strategy === 'prerender' && !failedStrategies.has('concat')) {
        return 'concat';
      }
      if (this.strategy === 'concat' && !failedStrategies.has('hybrid')) {
        return 'hybrid';
      }
      if (!failedStrategies.has('webcodecs')) {
        return 'webcodecs';
      }
      return 'hybrid';
    }

    // PRIORITÉ 0 : Si Burn Subtitles est demandé, on DOIT utiliser WebCodecs (seul capable de rendre le texte dans l'image)
    if (this.config.burnSubtitles && !failedStrategies.has('webcodecs')) {
      console.log(`[ExportManager] Burn Subtitles demandé -> Mode Temps Réel (WebCodecs)`);
      return 'webcodecs';
    }

    // PRIORITÉ 0.5 : Si mismatch de résolution détecté, on DOIT utiliser un mode de rendu (prerender ou webcodecs)
    // "Muxing" ou "Concat" avec -c copy ne supporteraient pas le crop demandé par l'utilisateur.
    const isMuxableResolution = await this.checkAllClipsDimensionsMatch(clips);
    if (!isMuxableResolution) {
      console.log(`[ExportManager] Resolution mismatch detected -> Mode Rendu requis pour Crop`);
      if (!failedStrategies.has('prerender')) return 'prerender';
      if (!failedStrategies.has('webcodecs')) return 'webcodecs';
    }

    // PRIORITÉ 1: Si AUCUN effet détecté, utiliser concat (ultra rapide, juste muxing)
    // C'est le mode "Instant" demandé par l'utilisateur pour les cuts simples
    const hasEffects = this.hasEffectsRequiringRender(clips, tracks);
    if (!hasEffects && !failedStrategies.has('concat')) {
      console.log(`[ExportManager] Aucun effet détecté -> Mode Concat (Instant Copy)`);
      return 'concat';
    }

    // PRIORITÉ 2 : Durée courte (< 10 minutes) -> Temps réel complet (WebCodecs)
    // Seulement si concat n'est pas possible (car effets)
    const timelineDuration = this.calculateTimelineDuration(clips);
    const SHORT_VIDEO_THRESHOLD = 120; // 2 minutes (120s)

    // Check for heavy effects that need smart hybrid routing
    // If > 2 min AND has heavy effects, prefer smart-hybrid
    const hasHeavyEffects = clips.some(c => ['overlay', 'synthe', 'panel', 'zoom', 'html', 'text', 'image'].includes(c.type));

    if (timelineDuration < SHORT_VIDEO_THRESHOLD && !failedStrategies.has('webcodecs')) {
      console.log(`[ExportManager] Vidéo courte avec effets (${timelineDuration.toFixed(1)}s < 600s) -> Mode Temps Réel (WebCodecs)`);
      return 'webcodecs';
    }

    if (timelineDuration >= SHORT_VIDEO_THRESHOLD && hasHeavyEffects && !failedStrategies.has('smart-hybrid')) {
      console.log(`[ExportManager] Vidéo longue avec effets -> Smart Hybrid`);
      return 'smart-hybrid';
    }

    // PRIORITÉ 3: Si des effets sont présents mais vidéo longue -> prerender
    if (hasEffects && !failedStrategies.has('prerender')) {
      return 'prerender';
    }

    if (!failedStrategies.has('hybrid')) {
      return 'hybrid';
    }

    if (!failedStrategies.has('webcodecs')) {
      return 'webcodecs';
    }

    // Final fallback
    return 'webcodecs';
  }

  private createExporter(
    strategy: ExportStrategy,
    progressCallback: ProgressCallback
  ): ExporterInstance {
    switch (strategy) {
      case 'muxing':
        return new MuxingExporter(this.config, progressCallback);
      case 'prerender':
        return new PreRenderMuxingExporter(this.config, progressCallback);
      case 'concat':
        return new ConcatExporter(this.config, progressCallback);
      case 'webcodecs':
        return new WebCodecsExporter(this.config, progressCallback);
      case 'hybrid':
        return new HybridExporter(this.config, progressCallback);
      case 'smart-hybrid':
        if (!this.config.userId || !this.config.projectId) {
          throw new Error('Smart Hybrid export requires userId and projectId');
        }
        // Adapter for SmartHybridExporter to match ExporterInstance
        const smartExporter = new SmartHybridExporter(
          this.config.userId,
          this.config.projectId,
          (p) => {
            // Map SmartHybrid progress to ExportManager progress
            let phase: any = 'encoding';
            if (p.phase === 'analyzing') phase = 'preparing';
            else if (p.phase === 'processing') phase = 'encoding';
            else if (p.phase === 'merging') phase = 'muxing';
            else if (p.phase === 'complete') phase = 'complete';
            else if (p.phase === 'failed') phase = 'error';

            progressCallback({
              phase,
              progress: p.progress,
              message: p.message,
              chunks: p.chunks // Pass chunks through
            });
          },
          this.config // Pass full ExportConfig so local chunks use correct aspect ratio & fps
        );
        return {
          export: (clips, tracks, activeSubtitleLanguage, subtitleStyle) => {
            return smartExporter.export(
              clips,
              tracks,
              activeSubtitleLanguage,
              subtitleStyle,
              this.config.burnSubtitles || false
            );
          },
          abort: () => smartExporter.abort()
        };
      default:
        return new HybridExporter(this.config, progressCallback);
    }
  }

  private shouldUseConcatExporter(clips: Clip[], tracks: Track[]): boolean {
    const videoClips = this.getVisibleVideoClips(clips, tracks);

    if (videoClips.length === 0) {
      return false;
    }

    const timelineDuration = this.calculateTimelineDuration(clips);
    const totalVideoDuration = videoClips.reduce((sum, clip) => sum + clip.duration, 0);
    const longestClip = videoClips.reduce(
      (max, clip) => (clip.duration > max ? clip.duration : max),
      0
    );

    return (
      timelineDuration >= this.concatDurationThreshold ||
      totalVideoDuration >= this.concatDurationThreshold ||
      longestClip >= this.longClipThreshold
    );
  }

  /**
   * Détecter si la timeline a des effets nécessitant un rendu
   * (zoom, split, text, images, intro/outro)
   */
  private hasEffectsRequiringRender(clips: Clip[], tracks: Track[]): boolean {
    const visibleClips = clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return track?.isVisible;
    });

    // Vérifier s'il y a des clips d'effet
    const hasZoom = visibleClips.some(c => c.type === 'zoom');
    const hasText = visibleClips.some(c => c.type === 'text');
    const hasImage = visibleClips.some(c => c.type === 'image');
    const hasSynthe = visibleClips.some(c => c.type === 'synthe');
    const hasPanel = visibleClips.some(c => c.type === 'panel');
    const hasSplit = visibleClips.some(c => c.trackId === 's1' && c.type === 'video');
    const hasIntro = visibleClips.some(c => c.clipRole === 'intro');
    const hasOutro = visibleClips.some(c => c.clipRole === 'outro');

    return hasZoom || hasText || hasImage || hasSynthe || hasPanel || hasSplit || hasIntro || hasOutro;
  }

  private calculateTimelineDuration(clips: Clip[]): number {
    if (!clips.length) {
      return 0;
    }

    return clips.reduce((maxEnd, clip) => {
      const clipEnd = clip.start + clip.duration;
      return clipEnd > maxEnd ? clipEnd : maxEnd;
    }, 0);
  }

  private getVisibleVideoClips(clips: Clip[], tracks: Track[]): Clip[] {
    return clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      if (!track?.isVisible) {
        return false;
      }

      if (clip.type !== 'video' || !clip.url) {
        return false;
      }

      // Intro/outro sont gérés comme overlays par le concat exporter
      if (clip.clipRole === 'intro' || clip.clipRole === 'outro') {
        return false;
      }

      return true;
    });
  }

  /**
   * Helper pour détecter le framerate d'une vidéo
   */
  private async detectFrameRate(url: string, file?: File): Promise<number> {
    try {
      // Fast-path: avoid expensive FFmpeg probing for remote URLs.
      // We'll keep the configured default FPS if probing is skipped.
      const isRemote = typeof url === 'string' && /^https?:\/\//i.test(url);
      if (isRemote && !file) {
        return 0;
      }

      // Utiliser FFmpegService pour une détection fiable (nécessite le chargement du fichier)
      // C'est un peu lourd mais c'est le seul moyen fiable 100% sans librairie tierce (mediainfo.js)

      // Soft-timeout court: no throw, just fallback to default FPS quickly.
      const timeoutPromise = new Promise<number>((resolve) => {
        setTimeout(() => resolve(0), 1200);
      });

      return await Promise.race([
        FFmpegService.detectFPS(url, file),
        timeoutPromise
      ]);
    } catch (e) {
      // Keep this silent-ish to avoid noisy logs for expected fallback paths.
      console.debug('[ExportManager] FPS probe skipped/fallback:', e);
      return 0;
    }
  }

  /**
   * Validate inputs
   */
  private validateInputs(clips: Clip[], tracks: Track[]): void {
    if (!clips || clips.length === 0) {
      throw new ExportError('Aucun clip à exporter');
    }

    if (!tracks || tracks.length === 0) {
      throw new ExportError('Aucune piste définie');
    }

    // Check if there are any visible clips
    const visibleClips = clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return track?.isVisible;
    });

    if (visibleClips.length === 0) {
      throw new ExportError('Aucun clip visible à exporter. Vérifiez que les pistes ne sont pas cachées.');
    }

    // Check WebCodecs support
    if (!('VideoEncoder' in window)) {
      throw new ExportError(
        'WebCodecs API non supportée. Veuillez utiliser un navigateur récent.'
      );
    }

    // Check if OffscreenCanvas is supported
    if (!('OffscreenCanvas' in window)) {
      throw new ExportError(
        'OffscreenCanvas non supporté. Veuillez utiliser un navigateur récent.'
      );
    }
  }

  /**
   * Check if error is retryable
   */
  private async checkAllClipsDimensionsMatch(clips: Clip[]): Promise<boolean> {
    const mainClips = clips.filter(c => c.type === 'video' && c.url && !c.clipRole);
    if (mainClips.length === 0) return true;

    // We only check the first main clip for now, assuming others follow or we want consistency
    const mainClip = mainClips[0];
    const resolution = this.config.aspectRatio ? ASPECT_RATIOS[this.config.aspectRatio] : { width: 1920, height: 1080 };

    if (!resolution) return true;

    return new Promise((resolve) => {
      const v = document.createElement('video');
      v.crossOrigin = 'anonymous';
      v.preload = 'metadata';
      v.onloadedmetadata = () => {
        const widthMatch = Math.abs(v.videoWidth - resolution.width) <= 1;
        const heightMatch = Math.abs(v.videoHeight - resolution.height) <= 1;
        resolve(widthMatch && heightMatch);
      };
      v.onerror = () => resolve(true); // Fallback to muxable if we can't check
      v.src = mainClip.url!;
    });
  }

  private isRetryableError(error: Error): boolean {
    if (error instanceof FFmpegOutOfMemoryError) {
      return true;
    }

    const message = error.message.toLowerCase();

    // Don't retry user aborts
    if (message.includes('annulé') || message.includes('abort')) {
      return false;
    }

    // Don't retry validation errors
    if (message.includes('non supporté') || message.includes('aucun clip')) {
      return false;
    }

    // Don't retry configuration errors
    if (message.includes('configuration') && message.includes('non supportée')) {
      return false;
    }

    // Retry network errors, temporary failures, etc.
    if (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('failed to fetch') ||
      message.includes('temporary')
    ) {
      return true;
    }

    // By default, retry unknown errors
    return true;
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ExportManagerConfig>): void {
    const {
      strategy,
      concatDurationThreshold,
      longClipThreshold,
      maxRetries,
      retryDelay,
      ...exportConfig
    } = config;

    if (strategy) {
      this.strategy = strategy;
    }

    if (typeof concatDurationThreshold === 'number') {
      this.concatDurationThreshold = concatDurationThreshold;
    }

    if (typeof longClipThreshold === 'number') {
      this.longClipThreshold = longClipThreshold;
    }

    if (typeof maxRetries === 'number') {
      this.maxRetries = maxRetries;
    }

    if (typeof retryDelay === 'number') {
      this.retryDelay = retryDelay;
    }

    this.config = {
      ...this.config,
      ...(exportConfig as Partial<ExportConfig>),
    };
  }

  /**
   * Get current configuration
   */
  getConfig(): ExportConfig {
    return { ...this.config };
  }
}

/**
 * Helper function to start an export
 */
export async function startExport(
  clips: Clip[],
  tracks: Track[],
  activeSubtitleLanguage: string,
  subtitleStyle: SubtitleStyle,
  config: ExportManagerConfig,
  progressCallback: ProgressCallback
): Promise<Blob> {
  const manager = new ExportManager(config);
  return await manager.export(
    clips,
    tracks,
    activeSubtitleLanguage,
    subtitleStyle,
    progressCallback
  );
}
